import type { MobileScreenSettingsWithRelations } from "@/types/mobile-settings";

type FeaturedSettings = Pick<
  MobileScreenSettingsWithRelations,
  "mobileScreenSettingsFeaturedActivities"
>;

/**
 * Service ids of a screen's Featured Activities list, in guest-app order
 * (ascending `order`, equal values keep the incoming sequence, a repeated
 * service counts once). Missing settings or relations give an empty list.
 */
export function featuredServiceIdsOf(
  settings: FeaturedSettings | null | undefined,
): string[] {
  const relations = settings?.mobileScreenSettingsFeaturedActivities ?? [];
  const sorted = [...relations].sort((a, b) => a.order - b.order);
  return Array.from(new Set(sorted.map((relation) => relation.serviceId)));
}

/**
 * The Featured list after turning one service on or off. On: appended to the
 * end unless it is already there. Off: removed. The order of the other
 * services stays. Always returns a new array.
 */
export function applyFeatured(
  ids: readonly string[],
  serviceId: string,
  featured: boolean,
): string[] {
  if (!featured) {
    return ids.filter((id) => id !== serviceId);
  }
  return ids.includes(serviceId) ? [...ids] : [...ids, serviceId];
}

/** True when both lists hold the same ids in the same order. */
export function isSameIdList(
  a: readonly string[],
  b: readonly string[],
): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

export interface FeaturedScreensIo<Screen> {
  getSettings: (screenType: Screen) => Promise<FeaturedSettings | null>;
  updateSettings: (
    screenType: Screen,
    featuredServiceIds: string[],
  ) => Promise<unknown>;
}

/**
 * Turns a service on or off in the Featured list of each screen, one screen
 * after another and in the given order: read the list fresh, apply the change,
 * skip the write when nothing changes, otherwise write. The first failed
 * request rejects the call; screens after it are not read or written. Calling
 * it again finishes the rest, because screens already in the wanted state are
 * skipped.
 */
export async function writeFeaturedToScreens<Screen>(
  screenTypes: readonly Screen[],
  serviceId: string,
  featured: boolean,
  io: FeaturedScreensIo<Screen>,
): Promise<void> {
  for (const screenType of screenTypes) {
    const current = featuredServiceIdsOf(await io.getSettings(screenType));
    const next = applyFeatured(current, serviceId, featured);
    if (isSameIdList(current, next)) {
      continue;
    }
    await io.updateSettings(screenType, next);
  }
}

const ignoreResult = (): void => undefined;

/**
 * Runs tasks of the same key one after another, in the order they were
 * added. A task starts after the previous one of its key has finished,
 * whether that one succeeded or failed; each caller gets the result of its
 * own task. Tasks of different keys do not wait for each other.
 */
export function createKeyedQueue(): <T>(
  key: string,
  task: () => Promise<T>,
) => Promise<T> {
  const tails = new Map<string, Promise<void>>();
  return <T>(key: string, task: () => Promise<T>): Promise<T> => {
    const previous = tails.get(key) ?? Promise.resolve();
    const run = previous.then(task);
    const tail = run.then(ignoreResult, ignoreResult);
    tails.set(key, tail);
    tail.then(() => {
      if (tails.get(key) === tail) {
        tails.delete(key);
      }
    });
    return run;
  };
}
